<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <AppModalDialog :hash="HASH" close-hash="profile">
    <Confirm
      v-if="step === 'confirm'"
      :purpose="purpose"
      :has-assistant="Boolean(assistant)"
      :error-message="confirmError"
      @submit="handleContinue"
      @update:canSubmit="confirmCanSubmit = $event"
    />
    <Verify
      v-else-if="step === 'verify'"
      :new-email="newEmail"
      :is-merge="isMerging"
      @success="onVerifySuccess"
      @update:canSubmit="verifyCanSubmit = $event"
    />
    <p v-if="step === 'verify' && finishError" class="error" role="alert">{{ finishError }}</p>
    <template #footer>
      <!-- how it works, just above Continue, as #sign-in's note (Seán, 2026-10-23, DL3) -->
      <p v-if="step === 'confirm' && purpose === 'replace' && !isMergeOffered" class="note">
        We'll send a verification code to your new address to confirm the change.
      </p>
      <ButtonBespoke
        v-if="step === 'confirm'"
        outstanding
        :disabled="!confirmCanSubmit || isSending"
        @click="submitConfirmForm"
      >
        {{ continueLabel }}
      </ButtonBespoke>
      <div v-else-if="step === 'verify'" class="footer-buttons">
        <ButtonBespoke @click="handleBack">◂ Back</ButtonBespoke>
        <ButtonBespoke
          outstanding
          :disabled="!verifyCanSubmit"
          @click="submitVerifyForm"
        >
          Continue ▸
        </ButtonBespoke>
      </div>
    </template>
  </AppModalDialog>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import AppModalDialog from "@/components/App/Modal/Dialog/index";
import ButtonBespoke from "@/components/ButtonBespoke";
import Confirm from "@/components/App/Modal/EmailChange/Confirm";
import Verify from "@/components/App/Modal/EmailChange/Verify";
import {useEmailChange} from "@/composables/useEmailChange";
import {postEmailAction} from "@/composables/useEmailEdit";
import useUserSafe from "@/composables/useUserSafe";
import {setAssistant, useAssistant} from "@/composables/useAssistant";

// #new-email: replace the primary; &secondary: add one (CLERK.md § Several emails per user).
// Only a verified address takes effect; leaving before that removes the unverified one.
// &assistant: name or change the assistant, another person's account (useAssistant): no code.
const HASH = /^new-email(&(secondary|assistant))?$/;

const route = useRoute();
const router = useRouter();
const {user} = useUserSafe();
const {newEmail, pendingEmailAddress, clearState} = useEmailChange();

const step = ref("confirm");
const isSending = ref(false);
const confirmError = ref("");
const confirmCanSubmit = ref(false);
const verifyCanSubmit = ref(false);
const finishError = ref("");
// the address is another account's (create: reason "taken"): Continue becomes Merge Accounts,
// which emails that address our code; the code step then merges (CLERK.md § Merging two accounts)
const isMergeOffered = ref(false);
const isMerging = ref(false);
const purpose = computed(() => route.hash.match(/&(secondary|assistant)$/)?.[1] || "replace");
const {assistant} = useAssistant();
const continueLabel = computed(() => {
  if (isMergeOffered.value) return "Merge Accounts ▸";
  if (purpose.value !== "assistant") return "Continue ▸";
  return assistant.value ? "Change Assistant ▸" : "Add Assistant ▸";
});

// an address added but not verified (Back, or the dialog closed): removed, so nothing half-done
// stays on the account
function removeUnverified() {
  const address = pendingEmailAddress.value;
  pendingEmailAddress.value = null;
  if (!address?.id || address.verification?.status === "verified") return;
  postEmailAction("remove", {emailAddressIds: [address.id]});
}

watch(
  () => route.hash,
  (hash) => {
    if (hash.startsWith("#new-email")) return;
    removeUnverified();
    isMergeOffered.value = false;
    isMerging.value = false;
    step.value = "confirm";
    confirmError.value = "";
    finishError.value = "";
    clearState();
  }
);

// another address: no merge to offer, and the last address's error no longer applies
watch(newEmail, () => {
  isMergeOffered.value = false;
  confirmError.value = "";
});

function handleBack() {
  removeUnverified();
  isMerging.value = false;
  finishError.value = "";
  step.value = "confirm";
}

function submitConfirmForm() {
  document.getElementById("email-new-form")?.requestSubmit();
}

function submitVerifyForm() {
  document.getElementById("email-verify-form")?.requestSubmit();
}

async function startMerge() {
  const data = await postEmailAction("merge-start", {email: newEmail.value});
  if (!data.ok) throw new Error(data.message);
  isMerging.value = true;
  step.value = "verify";
}

async function startAdding() {
  const data = await postEmailAction("create", {email: newEmail.value, purpose: purpose.value});
  if (data.reason === "taken") isMergeOffered.value = true;
  if (!data.ok) throw new Error(data.message);
  await user.value.reload();
  const emailAddress = user.value.emailAddresses.find((e) => e.id === data.emailAddressId);
  if (!emailAddress) throw new Error("Email address not found after creation");
  await emailAddress.prepareVerification({strategy: "email_code"});
  pendingEmailAddress.value = emailAddress;
  step.value = "verify";
}

// the assistant: linked by the server at once, then back to the email editor
async function startAssistant() {
  const result = await setAssistant(newEmail.value);
  if (!result.ok) throw new Error(result.message);
  clearState();
  router.push({hash: "#profile"});
}

function getStart() {
  if (purpose.value === "assistant") return startAssistant;
  return isMergeOffered.value ? startMerge : startAdding;
}

async function handleContinue() {
  if (isSending.value) return;
  isSending.value = true;
  confirmError.value = "";
  try {
    await getStart()();
  } catch (error) {
    confirmError.value = error?.message || "Failed to send code. Please try again.";
  } finally {
    isSending.value = false;
  }
}

// once verified: the new address replaces the primary; a secondary needs nothing more
function finishAction(emailAddressId) {
  if (purpose.value === "replace") {
    return postEmailAction("make-primary", {emailAddressId, replace: true});
  }
  return {ok: true};
}

// merged (merge-confirm did it): back to the Profile, which now shows the other account's addresses
async function finishMerge() {
  isMerging.value = false;
  isMergeOffered.value = false;
  await user.value?.reload();
  clearState();
  router.push({hash: "#profile"});
}

async function onVerifySuccess() {
  finishError.value = "";
  if (isMerging.value) return await finishMerge();
  const emailAddressId = pendingEmailAddress.value?.id;
  const result = emailAddressId ? await finishAction(emailAddressId) : {ok: true};
  await user.value?.reload();
  if (!result.ok) return (finishError.value = result.message);
  clearState();
  router.push({hash: "#profile"});
}
</script>

<style scoped>
/* room for the input's focus ring at the edges (the scroll area clips what's outside it); the
   negative margin keeps the content where it was (FORMS.md § 17; as #register) */
:deep(.modal-main) {
  padding-inline: 4px;
  margin-inline: -4px;
}
/* an aside, as #sign-in's note (SignIn/index.vue): centred, quieter than the text (#666 on the
   dialog's grey: 4.5:1) */
.note {
  margin: 6px 0 12px;
  text-align: center;
  font-size: 14px;
  line-height: 1.4; /* the colour: every footer note's (Dialog/index.vue) */
}
.error {
  color: #dc2204;
}
.footer-buttons {
  display: flex;
  gap: 0.75rem;
}
.footer-buttons > * {
  flex: 1;
}
</style>
